import React from "react";
import styles from "./Slide.module.css";
import type { NewsCategory } from "../../../../../types/News";
import { useNavigate } from "react-router-dom";

interface SlideProps {
  cover: string;
  category: NewsCategory;
  description: string;
  link: string;
  link_zewnetrzny: boolean;
  id: number;
  loading: boolean;
}

const truncateText = (text: string, limit: number) => {
  if (text.length > limit) return text.substring(0, limit) + "...";
  return text;
};

const extractNewsSlug = (rawLink: string) => {
  const trimmed = rawLink.trim();

  try {
    const u = new URL(trimmed);
    const path = u.pathname; // e.g. "/news/Premiera-..."
    const match = path.match(/\/news\/(.+)/i);

    if (match?.[1]) return match[1].replace(/\/+$/, "");
    return path.replace(/^\/+/, "").replace(/\/+$/, "");
  } catch {
    return trimmed
      .replace(/^\/+/, "")
      .replace(/^news\//i, "")
      .replace(/\/+$/, "");
  }
};

export const Slide = ({ cover, category, description, link, link_zewnetrzny, id, loading }: SlideProps) => {
  const navigate = useNavigate();

  const slug = extractNewsSlug(link);
  const internalTo = `/news/${slug}`;
  const externalHref = link;

  const navigateTo = () => {
    if (link_zewnetrzny) {
      // open external in new tab
      window.open(externalHref, "_blank", "noopener,noreferrer");
      return;
    }

    // internal SPA navigation
    navigate(internalTo);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigateTo();
    }
  };

  return (
    <article
      className={styles.slideContainer}
      style={{ backgroundImage: cover ? `url(${cover})` : `url(/mockup-background.jpg)` }}
      role="button"
      tabIndex={0}
      aria-label={`Read more about ${truncateText(description, 20)}`}
      onClick={navigateTo}
      onKeyDown={onKeyDown}
    >
      <div className={styles.link}>
        <div className={styles.coverImage} style={{ backgroundImage: `url(${cover})` }}>
          {description && <div className={styles.shadow} aria-hidden="true"></div>}
        </div>

        {description && (
          <section className={styles.textContainer}>
            <p className={`${styles.slideDescription} ${loading ? styles.slideDescriptionLoading : ""}`}>
              {truncateText(description, 150)}
            </p>
          </section>
        )}
      </div>
    </article>
  );
};
